import React, { useState } from "react";
import { Text } from "src/sudokit/components/Text";
import { Textarea } from "src/sudokit/components/Textarea";
import { SudoButton } from "src/sudokit/components/SudoButton";
import MenuSelect from "src/sudokit/components/SudoSelect/MenuSelect";
import { DEFAULT_IMAGE_GENERATION_MODEL_ID } from "~shared/imageGenerationModels";
import Field from "./shared/Field";
import ModelSelect, { modelSupportsReferenceImage } from "./shared/ModelSelect";
import PanelLayout from "./shared/PanelLayout";
import ReferenceImageField from "./shared/ReferenceImageField";
import ResultPanel from "./shared/ResultPanel";
import StylePresetEditor from "./shared/StylePresetEditor";
import { useImageGeneration } from "./shared/useImageGeneration";
import { useReferenceImage } from "./shared/useReferenceImage";
import { useStylePresets } from "./shared/useStylePresets";
import { CUSTOM_STYLE_PRESET_ID, WorldbuildingRoleOption } from "./shared/types";

const PRESETS_ENDPOINT = "/api/admin/imageGenTest/worldbuilding/presets";
const GENERATE_ENDPOINT = "/api/admin/imageGenTest/worldbuilding/generate";

// Reference photos of places/buildings are fine as png/jpeg/webp — same set the server accepts.
const ALLOWED_REFERENCE_IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

const AUTO_ASPECT = "auto";
// Radix Select rejects an option with value="", so the sentinel choices need non-empty values.
const NO_ROLE = "__none__";
const CUSTOM_ROLE = "__custom__";
// The Story Bible's own "Custom" role (optionValue "sudo-custom") is meaningless for image prompts —
// hide it and offer a free-text field instead.
const STORY_BIBLE_CUSTOM_ROLE = "sudo-custom";

const WorldbuildingPanel: React.FC = () => {
  const [name, setName] = useState("");
  const [roleChoice, setRoleChoice] = useState(NO_ROLE);
  const [customRole, setCustomRole] = useState("");
  const [details, setDetails] = useState("");
  const [genre, setGenre] = useState("");
  const [style, setStyle] = useState("");
  const [subjectOverride, setSubjectOverride] = useState("");
  const [aspectRatio, setAspectRatio] = useState<string>(AUTO_ASPECT);
  const [model, setModel] = useState<string>(DEFAULT_IMAGE_GENERATION_MODEL_ID);

  const presets = useStylePresets(PRESETS_ENDPOINT);
  const reference = useReferenceImage(ALLOWED_REFERENCE_IMAGE_MIME_TYPES);
  const { loading, imageUrl, result, error, generate } = useImageGeneration();

  const roles = ((presets.meta.roles as WorldbuildingRoleOption[] | undefined) ?? []).filter((r) => r.optionValue !== STORY_BIBLE_CUSTOM_ROLE);
  const aspectRatios = (presets.meta.aspectRatios as string[] | undefined) ?? [];

  const role = roleChoice === CUSTOM_ROLE ? customRole.trim() : roleChoice === NO_ROLE ? "" : roleChoice;

  const handleRoleChange = (value: string) => {
    setRoleChoice(value);
    const roleOption = roles.find((r) => r.optionValue === value);
    if (roleOption) {
      presets.setStylePresetId(roleOption.defaultStylePresetId);
      setAspectRatio(AUTO_ASPECT);
    }
  };

  const hasSummarizerInput = Boolean(subjectOverride.trim() || name.trim() || role.trim() || details.trim());
  const canGenerate =
    Boolean(presets.stylePresetId) &&
    hasSummarizerInput &&
    (presets.stylePresetId !== CUSTOM_STYLE_PRESET_ID || Boolean(presets.stylePresetText.trim()));

  const handleGenerate = () =>
    generate(GENERATE_ENDPOINT, {
      name: name || undefined,
      role: role || undefined,
      details: details || undefined,
      genre: genre || undefined,
      style: style || undefined,
      subject: subjectOverride.trim() || undefined,
      stylePresetId: presets.stylePresetId,
      stylePresetText: presets.stylePresetText || undefined,
      aspectRatio: aspectRatio === AUTO_ASPECT ? undefined : aspectRatio,
      referenceImage: modelSupportsReferenceImage(model) ? reference.referenceImage ?? undefined : undefined,
      model,
    });

  const conceptPreview = result
    ? [result.subject, result.mood ? `Mood & palette: ${result.mood}` : null].filter(Boolean).join("\n\n")
    : null;

  return (
    <PanelLayout
      result={<ResultPanel loading={loading} imageUrl={imageUrl} result={result} downloadName="worldbuilding-image-test.png" aspectRatio="3:2" conceptPreview={conceptPreview} />}
    >
      <div className="flex flex-col gap-3 w-full text-left">
        <Text size="sm" weight="bold">
          Worldbuilding Entry
        </Text>
        <Field id="wbName" label="Name" value={name} onChange={setName} optional placeholder="e.g. The Sunken Market of Vael" />

        <div className="flex flex-col gap-2 w-full">
          <label className="text-sm font-medium">
            Type / Role <span className="text-app-grey-1 font-normal">(optional)</span>
          </label>
          <MenuSelect
            options={[
              { value: NO_ROLE, label: "— none —" },
              { value: CUSTOM_ROLE, label: "Custom (type your own)…" },
              ...roles.map((r) => ({ value: r.optionValue, label: r.name })),
            ]}
            value={roleChoice}
            onChange={handleRoleChange}
            label="Type / Role"
            variant="secondary"
            menuAlign="start"
          />
          {roleChoice === CUSTOM_ROLE && (
            <Field id="wbCustomRole" label="Custom role" value={customRole} onChange={setCustomRole} placeholder="e.g. city, faction, deity, creature" />
          )}
          <Text size="xs" color="grey-1">
            Picking a role sets a sensible default style variant and aspect ratio. A custom role is matched on keywords server-side (e.g. "city" → landscape, "artifact" → square).
          </Text>
        </div>

        <Field id="wbDetails" label="Details" value={details} onChange={setDetails} optional multiline minRows={5} placeholder="Description, location, history — the AI-visible custom fields, as plain text" />
        <Field id="wbGenre" label="Genre" value={genre} onChange={setGenre} optional placeholder="e.g. grimdark fantasy" />
        <Field id="wbStyle" label="Style" value={style} onChange={setStyle} optional placeholder="e.g. lush, foreboding" />
      </div>

      <div className="flex flex-col gap-2 w-full text-left border-t border-app-menu-color-hover pt-6">
        <label htmlFor="wbSubjectOverride" className="text-sm font-medium">
          Subject override <span className="text-app-grey-1 font-normal">(optional)</span>
        </label>
        <Textarea mode="default" id="wbSubjectOverride" value={subjectOverride} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSubjectOverride(e.target.value)} placeholder="Type a visual subject directly to skip the summarizer" minRows={3} />
        <Text size="xs" color="grey-1">
          Leave blank to summarize a visual subject from name / role / details.
        </Text>
      </div>

      <div className="flex flex-col gap-2 w-full text-left">
        <Text size="sm" weight="medium">
          Aspect Ratio
        </Text>
        <MenuSelect
          options={[{ value: AUTO_ASPECT, label: "Auto (from role)" }, ...aspectRatios.map((ratio) => ({ value: ratio, label: ratio }))]}
          value={aspectRatio}
          onChange={setAspectRatio}
          label="Aspect ratio"
          variant="secondary"
          menuAlign="start"
        />
      </div>

      <StylePresetEditor
        presets={presets.presets}
        presetsLoading={presets.presetsLoading}
        presetsError={presets.presetsError}
        stylePresetId={presets.stylePresetId}
        onStylePresetIdChange={presets.setStylePresetId}
        text={presets.stylePresetText}
        onTextChange={presets.setStylePresetText}
        onResetText={presets.resetStylePresetText}
        isTextDefault={presets.isStylePresetTextDefault}
        promptHelpText="Edit this to iterate on the environment style wording — sent as-is with the generated subject/mood."
      />

      {modelSupportsReferenceImage(model) && <ReferenceImageField field={reference} label="Reference Image (optional — a real photo of a place, building, or object)" />}

      <ModelSelect value={model} onChange={setModel} aspectRatioAware helpText="Recraft and FLUX are hidden here — they ignore the aspect ratio." />

      <SudoButton type="button" variant="primary" state={loading ? "loading" : "default"} disabled={loading || !canGenerate} onClick={handleGenerate}>
        {loading ? "Generating..." : "Generate"}
      </SudoButton>

      {error && (
        <Text size="sm" color="danger-color">
          {error}
        </Text>
      )}
    </PanelLayout>
  );
};

export default WorldbuildingPanel;
